import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Bot, Building2, CalendarCheck, ChartNoAxesCombined, CheckCircle2, CircleHelp, ContactRound, CreditCard, Headset, LayoutDashboard, LifeBuoy, LockKeyhole, Menu, MessageSquare, Mic, Moon, Phone, PhoneIncoming, Search, Settings, Sun, Users, Workflow, X } from 'lucide-react';
import { tenantAPI } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { BASIC_SECTION_FEATURES } from '../../lib/features';
import { goTo } from '../../lib/host';
import { useT } from '../../lib/i18n';
import { usePlanLabel } from '../../lib/usePlans';
import '../BasicDashboard.css';
import '../BasicDashboardDark.css';
import NotificationBell from '../../components/notifications/NotificationBell';
import WelcomeTour from '../../components/tour/WelcomeTour';
import { PreferencesProvider, usePreferences } from './preferences/PreferencesContext';
import './preferences/preferences.css';
import ProfileMenu from './shell/ProfileMenu';
import './shell/shell.css';
import useIdleSignOut from './shell/useIdleSignOut';

const ROLE_LABELS = { owner: 'Company Owner', admin: 'Company Admin', member: 'Team Member', viewer: 'Viewer' };

const NAV = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/ai-receptionist', label: 'AI Receptionist', Icon: Bot },
  { to: '/phone-numbers', label: 'Phone Numbers', Icon: Phone },
  { to: '/call-flows', label: 'Call Flows', Icon: Workflow },
  { to: '/calls', label: 'Calls & Conversations', Icon: PhoneIncoming },
  { to: '/recordings', label: 'Call Recordings', Icon: Mic },
  { to: '/contacts', label: 'Contacts', Icon: ContactRound },
  { to: '/appointments', label: 'Appointments', Icon: CalendarCheck },
  { to: '/campaigns', label: 'Campaigns', Icon: MessageSquare, locked: true },
  { to: '/analytics', label: 'Analytics', Icon: ChartNoAxesCombined },
  { to: '/team', label: 'Team', Icon: Users },
  { to: '/billing', label: 'Billing & Plan', Icon: CreditCard },
  { to: '/settings', label: 'Settings', Icon: Settings },
  { to: '/support', label: 'Help & Support', Icon: LifeBuoy },
];

/** Today's date and time (updates every 30 s) and the light / dark switch, remembered in this browser. */
function ClockAndTheme({ dark, onToggle }) {
  const { t } = useT();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="datebox" aria-label="Date and time">
        <small>{now.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</small>
        <b>{now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</b>
      </div>
      <button className="icon-button theme-toggle" title={dark ? t('Switch to light mode') : t('Switch to dark mode')} aria-label={dark ? t('Switch to light mode') : t('Switch to dark mode')}
        aria-pressed={dark} onClick={onToggle}>{dark ? <Sun size={18} /> : <Moon size={18} />}</button>
    </>
  );
}

const UPGRADE_POINTS = ['WhatsApp & SMS', 'More AI Agents', 'Advanced Analytics', 'More Phone Numbers', 'Campaigns & Automation'];
const TODAY = "Here's what's happening with your business today.";
const CAUGHT_UP = "You're all caught up.";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/** The Business Basic shell, with the person's own Settings (theme, language, accessibility…) applied. */
export default function BasicLayout() {
  return <PreferencesProvider><BasicShell /></PreferencesProvider>;
}

function BasicShell() {
  const { t } = useT();
  const preferences = usePreferences();
  const planLabel = usePlanLabel();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { tenant, role, can, hasFeature, tour } = useTenant();
  // Sections the person may see and the plan includes. Without the AI receptionist its page still sets the greeting
  // and office hours, so it stays as "Call Answering".
  const nav = NAV.filter((n) => (!n.perm || can(n.perm)) && hasFeature(BASIC_SECTION_FEATURES[n.to]))
    .map((n) => (n.to === '/ai-receptionist' && !hasFeature('ai_receptionist') ? { ...n, label: 'Call Answering' } : n));
  const [notice, setNotice] = useState(location.state?.success || '');
  const [mobileMenu, setMobileMenu] = useState(false);
  const [memberCount, setMemberCount] = useState(null);
  const { dark, attrs, avatar, prefs } = preferences;
  const toggleTheme = useCallback(() => preferences.save('appearance', { theme: dark ? 'light' : 'dark' }).catch(() => {}), [preferences, dark]);

  const refreshMembers = useCallback(() => {
    tenantAPI.get().then((res) => setMemberCount(res.data.member_count)).catch(() => {});
  }, []);
  useEffect(refreshMembers, [refreshMembers]);
  useEffect(() => setMobileMenu(false), [location.pathname]);

  const upgrade = useCallback(() => {
    if (can('billing.manage')) navigate('/billing');
    else setNotice('Ask your company owner to change the plan.');
  }, [can, navigate]);

  // The welcome tour: the first time someone opens this company's dashboard, and from Settings (?tour=replay).
  const replayTour = new URLSearchParams(location.search).get('tour') === 'replay';
  const [tourClosed, setTourClosed] = useState(false);
  const closeTour = useCallback((action) => {
    setTourClosed(true);
    tenantAPI.saveTour(action).catch(() => {}); // not saved: it shows again next time, nothing else breaks
    if (action === 'finish') navigate('/', { replace: true });
    else if (replayTour) navigate(location.pathname, { replace: true });
  }, [navigate, replayTour, location.pathname]);
  useEffect(() => { if (replayTour) setTourClosed(false); }, [replayTour]);

  const handleLogout = useCallback(async () => {
    await logout();
    goTo('/login');  // this company's own login page
  }, [logout]);
  useIdleSignOut(prefs.security?.session_timeout, handleLogout);

  // The flow builder needs the full height, so it drops the greeting banner and footer.
  const inBuilder = /^\/call-flows\/[^/]+/.test(location.pathname);
  const person = user.name;
  const firstName = person.split(' ')[0];
  const plan = planLabel(tenant.plan);
  const upgradePlan = planLabel('business');
  const roleLabel = ROLE_LABELS[role] || role;

  // dark / toggleTheme: pages with their own full-screen view (the call flow builder) switch the same light / dark setting.
  const ctx = useMemo(() => ({
    notify: setNotice, upgrade, plan, upgradePlan, company: tenant.name, person, roleLabel, memberCount, refreshMembers, dark, toggleTheme,
  }), [upgrade, plan, upgradePlan, tenant.name, person, roleLabel, memberCount, refreshMembers, dark, toggleTheme]);

  return (
    <div className={`tcx-basic ${dark ? 'dark' : ''}`} {...attrs}>
      {(replayTour || tour?.show) && !tourClosed && (
        <WelcomeTour companyName={tenant.name} userName={user.name} logoSrc="/brand/telecalx-logo.webp" has={hasFeature} onClose={closeTour} />
      )}
      <div className="app-shell">
        <aside className={`sidebar ${mobileMenu ? 'sidebar-open' : ''}`}>
          <div className="brand"><img className="brand-logo" src="/brand/telecalx-logo.webp" alt="TeleCalX — The Future of Business Calling" width={720} height={184} /></div>
          <div className="workspace">
            <span className="workspace-icon"><Building2 size={23} /></span>
            <span className="workspace-copy"><b>{tenant.name}</b><small>{plan} <CheckCircle2 size={12} fill="#16a34a" color="#fff" /></small></span>
          </div>
          <nav className="main-nav">
            {nav.map(({ to, label, Icon, locked }) => (
              <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                <Icon size={19} strokeWidth={2.2} /><span>{t(label)}</span>{locked && <LockKeyhole className="lock-icon" size={15} />}
              </NavLink>
            ))}
          </nav>
          <div className="upgrade-card">
            <div className="upgrade-title"><span className="crown">♛</span><b>{t('Upgrade to {plan}', { plan: upgradePlan })}</b></div>
            <ul>{UPGRADE_POINTS.map((f) => <li key={f}>{t(f)}</li>)}</ul>
            <button onClick={upgrade}>{t('View Plans')} <ArrowRight size={14} /></button>
          </div>
          <div className="help-card">
            <div className="help-head"><Headset size={24} /><div><b>{t('Need Help?')}</b><small>{t('Chat with our support team')}</small></div></div>
            <button onClick={() => navigate('/support?new=1')}>{t('Open Support Ticket')}</button>
          </div>
        </aside>

        <main className="main-area">
          <header className="topbar">
            <button className="mobile-toggle icon-button" aria-label="Menu" onClick={() => setMobileMenu(!mobileMenu)}><Menu size={20} /></button>
            <div className="searchbox"><Search size={16} /><input placeholder={t('Search contacts, calls, appointments...')} aria-label={t('Search')} /><kbd>⌘K</kbd></div>
            <div className="top-actions">
              <ClockAndTheme dark={dark} onToggle={toggleTheme} />
              <button className="icon-button" title={t('Help & Support')} aria-label={t('Help & Support')} onClick={() => navigate('/support')}><CircleHelp size={19} /></button>
              <NotificationBell className="icon-button bell" sound={!!prefs.notifications?.sound} desktop={!!prefs.notifications?.desktop}
                labels={{ title: t('Notifications'), markAll: t('Mark all read'), empty: t(CAUGHT_UP) }} />
              <ProfileMenu name={person} email={user.email} role={t(roleLabel)} avatar={avatar} canBill={can('billing.manage')} onSignOut={handleLogout} />
            </div>
          </header>

          {notice && (
            <div className="notice" role="status">
              <span>{notice}</span>
              <button aria-label="Dismiss" onClick={() => setNotice('')}><X size={14} /></button>
            </div>
          )}

          {!inBuilder && (
            <section className="welcome-row">
              <div><h1>{t(greeting())}, {firstName}! <span>👋</span></h1><p>{t(TODAY)}</p></div>
              <div className="plan-banner">
                <div className="star-bubble">★</div>
                <div className="plan-copy"><b>{plan}</b><small>{t('AI-powered calling for growing businesses')}</small></div>
                <button onClick={upgrade}>{t('Upgrade Plan')} <ArrowRight size={15} /></button>
                <div className="plant">🌱</div>
              </div>
            </section>
          )}

          <Outlet context={ctx} />

          {!inBuilder && <footer>© {new Date().getFullYear()} TeleCalX Technologies <span>•</span> {t('The Future of Business Calling')}</footer>}
        </main>
      </div>
    </div>
  );
}
